import { slicedToArray as _slicedToArray, extends as _extends } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useCallback } from 'react';
import { useSlate } from 'slate-react';
import { Editor, Element, Transforms } from 'slate';
import { ToolbarButton } from './toolbar-button.js';
import { Spacer } from '@chakra-ui/react';
import { i18n } from '../../../../i18n/index.js';
import { htmlToSlateJson, jsonToSlateHtml } from '../utils/format-converters.js';
import { tokens } from '@everfi/design-system';
import { resetNodes, isBlockActive, isMarkActive, toggleBlock, toggleMark, toggleLink } from '../utils/index.js';
import { Types } from '../utils/contants.js';
import { Layout } from '../../Layout/component.js';

var Toolbar = function Toolbar(_ref) {
  var isCode = _ref.isCode,
      setIsCode = _ref.setIsCode,
      _ref$customButtons = _ref.customButtons,
      customButtons = _ref$customButtons === void 0 ? [] : _ref$customButtons,
      allowCodeToggle = _ref.allowCodeToggle;
  var editor = useSlate();
  var setData = useCallback(function (data) {
    var _data$;

    setIsCode(((_data$ = data[0]) === null || _data$ === void 0 ? void 0 : _data$.type) === Types.codeBlock);
    resetNodes(editor, {
      nodes: data
    });
  }, []);
  var onToggled = useCallback(function (value) {
    if (isCode) {
      // CONVERT TO SLATE JSON FROM HTML
      setData(htmlToSlateJson(editor));
    } else {
      // CONVERT TO HTML FROM SLATE JSON
      setData(jsonToSlateHtml(editor));
    }
  }, [isCode]);
  /*********************************************************************************
   * CUSTOM BUTTON FROM PROPS
   *********************************************************************************/

  var CustomBtn = function CustomBtn(_ref2) {
    var isActive = _ref2.isActive,
        icon = _ref2.icon,
        handlerCallback = _ref2.handlerCallback;
    return /*#__PURE__*/React.createElement(ToolbarButton, {
      isDisabled: isCode,
      isActive: isActive,
      type: "custom",
      icon: icon,
      handler: useCallback(function (event) {
        event.preventDefault();
        handlerCallback(event);
      }, [])
    });
  };
  /*********************************************************************************
   * STANDARD BUTTON FOR THE BAR
   *********************************************************************************/


  var BarBtn = function BarBtn(_ref3) {
    var format = _ref3.format,
        icon = _ref3.icon,
        type = _ref3.type;
    return /*#__PURE__*/React.createElement(ToolbarButton, {
      isDisabled: isCode && type !== 'code',
      isActive: type === 'block' ? isBlockActive(editor, format) : isMarkActive(editor, format),
      type: format,
      icon: icon,
      handler: useCallback(function (event) {
        event.preventDefault();
        type === 'block' ? toggleBlock(editor, format) : toggleMark(editor, format);
      }, [format])
    });
  };
  /*********************************************************************************
   * LINK POPUP BUTTON
   *********************************************************************************/


  var LinkBtn = function LinkBtn(_ref4) {
    var icon = _ref4.icon,
        type = _ref4.type;
    var handleMouseDown = useCallback(function (event) {
      event.preventDefault();
      var selection = editor.selection;

      if (selection) {
        var _Editor$nodes = Editor.nodes(editor, {
          match: function match(n) {
            return !Editor.isEditor(n) && Element.isElement(n) && n.type === Types.link;
          }
        }),
            _Editor$nodes2 = _slicedToArray(_Editor$nodes, 1),
            link = _Editor$nodes2[0];

        if (link) {
          Transforms.unwrapNodes(editor, {
            match: function match(n) {
              return !Editor.isEditor(n) && Element.isElement(n) && n.type === Types.link;
            }
          });
        } else {
          var url = window.prompt(i18n.t('Enter the URL of the link:'), '');
          if (url) toggleLink(editor, url);
        }
      }
    }, []);
    return /*#__PURE__*/React.createElement(ToolbarButton, {
      isDisabled: isCode && type !== 'code',
      isActive: isMarkActive(editor, Types.link),
      type: Types.link,
      icon: icon,
      handler: handleMouseDown
    });
  };

  var renderCustomButtons = function renderCustomButtons() {
    return customButtons.map(function (buttonProps, index) {
      return /*#__PURE__*/React.createElement(CustomBtn, _extends({
        key: index
      }, buttonProps));
    });
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Layout, {
    gap: "xs",
    variant: "flex",
    padding: tokens.SpacingBaseXs,
    border: "".concat(tokens.BorderWidthXs, " solid ").concat(tokens.ColorsGrey200),
    borderTopWidth: 0,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    bg: "surface_inset",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(BarBtn, {
    type: "mark",
    format: "bold",
    icon: "bold"
  }), /*#__PURE__*/React.createElement(BarBtn, {
    type: "mark",
    format: "italic",
    icon: "italic"
  }), /*#__PURE__*/React.createElement(BarBtn, {
    type: "block",
    format: "heading-one",
    icon: "h1"
  }), /*#__PURE__*/React.createElement(BarBtn, {
    type: "block",
    format: "heading-two",
    icon: "h2"
  }), /*#__PURE__*/React.createElement(LinkBtn, {
    type: Types.link,
    icon: "link"
  }), renderCustomButtons(), /*#__PURE__*/React.createElement(Spacer, null), allowCodeToggle && /*#__PURE__*/React.createElement(ToolbarButton, {
    isActive: isCode,
    type: "codeView",
    handler: onToggled,
    text: i18n.t('Edit HTML')
  })));
};

export { Toolbar };